/* ============================================================
   SINGSPACE ADMIN — DEVIS (QUOTES) API INTEGRATED
   ============================================================ */

import { CheckCircle2, ChevronLeft, ChevronRight, FileText, Send, XCircle } from 'lucide-react';
import { KPIWidget } from '../../components/common/KPIWidget';
import { QuotesFilters } from '../../components/quotes/QuotesFilters';
import { QuotesTable } from '../../components/quotes/QuotesTable';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useQuotesAdmin } from '../../hooks/useQuotesAdmin';
import { cn } from '../../lib/utils';

export function QuotesPage() {
  const {
    quotes,
    loading,
    error,
    pagination,
    stats,
    statusFilter,
    typeFilter,
    searchQuery,
    updateFilter,
    updateSearch,
    resetFilters,
    setPage,
  } = useQuotesAdmin();

  if (loading) {
    return (
      <div className="space-y-6 pb-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Devis</h1>
            <p className="text-slate-500 mt-1">Consultez et envoyez les devis personnalisés</p>
          </div>
        </div>
        <LoadingSpinner message="Chargement des devis" submessage="Veuillez patienter..." size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 pb-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Devis</h1>
            <p className="text-slate-500 mt-1">Consultez et envoyez les devis personnalisés</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium mb-2">Erreur lors du chargement</p>
          <p className="text-red-500 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-8 sm:pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">Devis</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">Consultez et envoyez les devis personnalisés</p>
        </div>
        <button className="w-full sm:w-auto bg-primary text-white hover:bg-primary/90 px-4 py-2 rounded-md font-medium text-sm transition-colors shadow-sm">
          Créer un devis
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KPIWidget
          title="Total Devis"
          value={stats.total.toString()}
          subtext="Toutes périodes"
          trend="up"
          color="primary"
          icon={<FileText className="w-4 sm:w-5 h-4 sm:h-5" />}
        />
        <KPIWidget
          title="Envoyés"
          value={stats.sent.toString()}
          subtext="Relance suggérée"
          trend="down"
          color="warning"
          icon={<Send className="w-4 sm:w-5 h-4 sm:h-5" />}
        />
        <KPIWidget
          title="Acceptés"
          value={stats.accepted.toString()}
          subtext="Prêts à facturer"
          trend="up"
          color="success"
          icon={<CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5" />}
        />
        <KPIWidget
          title="Refusés / Expirés"
          value={stats.expiredRejected.toString()}
          subtext="Opportunités perdues"
          trend="down"
          color="error"
          icon={<XCircle className="w-4 sm:w-5 h-4 sm:h-5" />}
        />
      </div>

      <div className="flex flex-col gap-4">
        <QuotesFilters
          search={searchQuery}
          onSearchChange={updateSearch}
          statusFilter={statusFilter}
          onStatusChange={(v) => updateFilter('status', v)}
          typeFilter={typeFilter}
          onTypeChange={(v) => updateFilter('type', v)}
          onReset={resetFilters}
          hasActiveFilters={statusFilter !== 'all' || typeFilter !== 'all' || searchQuery.trim() !== ''}
          filteredCount={quotes.length}
          totalCount={pagination.total}
        />
        <QuotesTable
          quotes={quotes}
          hasActiveFilters={statusFilter !== 'all' || typeFilter !== 'all' || searchQuery.trim() !== ''}
          onReset={resetFilters}
        />
        <Pagination
          totalPages={pagination.totalPages}
          currentPage={pagination.page}
          onPageChange={setPage}
          filteredCount={pagination.total}
        />
      </div>
    </div>
  );
}

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  filteredCount: number;
}

function Pagination({ totalPages, currentPage, onPageChange, filteredCount }: PaginationProps) {
  if (totalPages <= 1) return null;
  return (
    <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-b-xl">
      <span className="text-sm text-slate-500 font-medium">
        Page {currentPage} sur {totalPages} · {filteredCount} résultats
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-white disabled:opacity-40 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded border text-sm font-medium transition-colors',
              p === currentPage
                ? 'bg-primary text-white border-primary shadow-sm'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-white disabled:opacity-40 transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
